/**
 * Opened from the iPhone or Android app (docs/APP-REVIEW.md "Purchases"). The phone opens our pages in its in-app browser
 * with ?via=app (apps/mobile logic/storePurchase.ts withViaApp). Apple 3.1.3(f) and Google Play's payments policy: a free
 * companion app may not lead to a purchase outside the store's billing. So for this browser tab: no card ask, no banner
 * with a card button, no Stripe Checkout or customer portal, no "Start 2 free weeks". Everything else works as usual.
 * Kept in sessionStorage: the tab remembers it while the person moves around; a normal browser visit never has it.
 */
const KEY = "cc.viaApp";

export function noteViaApp(search: string, store: Pick<Storage, "setItem"> | undefined = globalThis.sessionStorage): boolean {
  try {
    if (new URLSearchParams(search).get("via") !== "app") return false;
    store?.setItem(KEY, "1");
    return true;
  } catch { return false; }
}

export function viaApp(store: Pick<Storage, "getItem"> | undefined = globalThis.sessionStorage): boolean {
  try { return store?.getItem(KEY) === "1"; } catch { return false; }
}

/** Said where a card button or checkout would be. No link, no price. */
export const PLAN_NOT_HERE = "Plan and billing aren't shown in the app.";

/** The api with every purchase call refused in the phone app's browser: Checkout, the card portal, keeping a trial, turning an add-on on. */
export function withoutPurchases<A extends {
  billingCheckout: (...a: never[]) => Promise<unknown>; billingPortal: (...a: never[]) => Promise<unknown>;
  keepFeature: (...a: never[]) => Promise<unknown>; setAddons: (orgId: string, addons: { aiPhotos: boolean }) => Promise<unknown>;
}>(api: A, inApp: () => boolean = viaApp): A {
  const no = () => Promise.reject(new Error(PLAN_NOT_HERE));
  return {
    ...api,
    billingCheckout: ((...a: never[]) => (inApp() ? no() : api.billingCheckout(...a))) as A["billingCheckout"],
    billingPortal: ((...a: never[]) => (inApp() ? no() : api.billingPortal(...a))) as A["billingPortal"],
    keepFeature: ((...a: never[]) => (inApp() ? no() : api.keepFeature(...a))) as A["keepFeature"],
    setAddons: ((orgId: string, addons: { aiPhotos: boolean }) => (inApp() && addons.aiPhotos ? no() : api.setAddons(orgId, addons))) as A["setAddons"],
  };
}
